import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { EMPTY, Observable, of, throwError } from 'rxjs';
import { catchError, exhaustMap, filter, map, tap } from 'rxjs/operators';

import { ToastService } from '@libs/src/toast/toast.service';

import {
  Opportunity,
  OpportunityState,
  OpportunitySubmission,
} from './opportunity.model';
import { IAppState } from '@main-client/src/app/app.state';
import {
  AddToSetOpportunitiesState,
  GetOpportunity,
  GetOpportunityFail,
} from '@main-client/src/app/opportunity/opportunity.actions';
import { findOpportunityStateBySlug } from '@main-client/src/app/opportunity/opportunity.helper';

import get from 'lodash-es/get';
import isEmpty from 'lodash-es/isEmpty';

const OPPORTUNITY_API_URL = 'api/opportunity';
const OPPORTUNITIES_API_URL = 'api/v2/opportunities';

@Injectable()
export class OpportunityStoreService {
  opportunitiesStore = this.store.select('opportunities');
  userState = this.store.select('user');

  constructor(
    private http: HttpClient,
    private store: Store<IAppState>,
    private toastService: ToastService,
  ) {}

  getOpportunityStateFromStore(slug: string): Observable<any> {
    return this.opportunitiesStore.pipe(
      filter((opportunityStates: OpportunityState[]) => {
        const opportunityState = findOpportunityStateBySlug(
          opportunityStates,
          slug,
        );
        return (
          isEmpty(opportunityStates) ||
          isEmpty(opportunityState) ||
          (!isEmpty(opportunityState) && !opportunityState.isLoading)
        );
      }),
      exhaustMap((opportunityStates: OpportunityState[]) => {
        const opportunityState = findOpportunityStateBySlug(
          opportunityStates,
          slug,
        );
        const opportunityInStore =
          !isEmpty(opportunityStates) &&
          !get(opportunityState, 'isLoading', false) &&
          get(opportunityState, 'isLoaded', false);
        if (opportunityInStore) {
          return of(opportunityState);
        }
        this.store.dispatch(GetOpportunity({ slug }));
        return this.getOpportunity(slug).pipe(
          map((opportunity: Opportunity) => {
            return {
              document: opportunity,
              isLoading: false,
              isLoaded: true,
            };
          }),
        );
      }),
    );
  }

  getOpportunity(slug: string) {
    return this.http.get(`${OPPORTUNITY_API_URL}/${slug}`).pipe(
      map((response: { data: Opportunity }) => response.data),
      exhaustMap((opportunity: Opportunity) =>
        this.getOpportunityWithSubmissions(opportunity),
      ),
      tap((opportunityWithSubmissions: Opportunity) => {
        this.store.dispatch(
          AddToSetOpportunitiesState(opportunityWithSubmissions),
        );
      }),
      catchError((response: any) => {
        const error = get(response, 'error.message', response.message);
        this.store.dispatch(
          GetOpportunityFail({ opportunity: { slug }, error }),
        );
        return EMPTY;
      }),
    );
  }

  getOpportunityWithSubmissions(opportunity: Opportunity) {
    return this.getOpportunitySubmissions(opportunity).pipe(
      map((submissions: OpportunitySubmission[]) => {
        return {
          ...opportunity,
          submissions,
        };
      }),
      catchError(() => of({ ...opportunity })),
    );
  }

  getOpportunitySubmissions(opportunity: Opportunity) {
    const params = {
      status: ['active', 'inactive'],
    };
    return this.http
      .get(`${OPPORTUNITIES_API_URL}/${opportunity._id}/submissions`, {
        params,
      })
      .pipe(
        catchError((response: any) => {
          const error = get(response, 'error.message', response.message);
          this.toastService.showError(error);
          return throwError(error);
        }),
      );
  }
}
